import {
  FrameRange,
  Interval,
  Point,
  Rectangle,
} from 'content-creation-common/models';
import { getChapterBreaks } from '../util/chapterBreaks';
import ClampedNumber from '../../../model/ClampedNumber';
import Constraints from '../../../model/Constraints';
import Content from '../../../model/Content';
import Layer from '../../../model/Layer';
import Medium from '../../../model/Medium';
import { isNil, isNotNil } from '../../../util/object';
import {
  areNumbersEqualToDecimal,
  clamp,
  floorForPrecision,
  isNotNumber,
  isNumber,
} from '../../../util/numbers';
import * as Log from '../../../util/log';
import { captureDebug, captureDebugOnce } from '../../../util/sentry';
import {
  isEmpty as isEmptyArray,
  sortBy,
  sortByProperties,
} from '../../../util/array';
import {
  CONTENT_TYPE,
  DEFAULT_FILL_COLOR,
  MEDIA_TYPE,
  PLAYBACK_FRAMES_PER_SECOND,
  SHAPE,
} from '../../../constants';
import {
  ALMOST_A_THIRD_SPLIT,
  CHAPTER_LENGTH_MAX_SECOND,
  CONTENT_TIME_PRECISION,
  DEFAULT_BLUR_FILTER_VALUE_PX,
  DEFAULT_CHAPTER_LENGTH_TARGET_SECOND,
  FIXED_PRECISION_FRAME_DURATION,
  FOUR_FIVE_RATIO,
  MAX_SCALE_MULTIPLIER,
  MEDIA_SEGMENT_EDIT_DEFAULT_LAYOUTS,
  MEDIA_SEGMENT_EDIT_LAYOUTS,
  SHOT_BACKGROUND_TYPES,
  SHOT_DIVIDER_HEIGHT,
  SHOT_ELEMENT_TYPES,
  SHOT_VIEW_HEIGHT_FIT_WIDTH,
  SINGLE_VIEW_FULL_HEIGHT_RATIO,
} from '../constants';
import { DEFAULT_KEYFRAME_NUMBER } from '../../creative/constants';
import { GROUP_ROLES } from '../../group/constants';
import { isGroup } from '../../group/util';
import { ALPHA_BLEND_MODES } from '../../../constants/AlphaBlendModes';

import ChapterUtilWorkerService from '../worker/ChapterUtilWorkerService';
import { MEDIA_SEGMENT_EDIT_TYPE } from '../../../model/media/constants';
import ShotBackground from '../../../model/shot/ShotBackground';
import { getMediaRatioForBounds } from '../../../util/mediaUtils';
import MediaSegmentTransformationControlMetadata from '../../../model/media/MediaSegmentTransformationControlMetadata';
import {
  getByIdHelper,
  getContentHelper,
  getContentIdHelper,
  getLayerByIdHelper,
  getMediaHelper,
} from '../../../selectors/element';
import {
  DECIMAL_PRECISION_FOR_COMPARISON_OF_TRANSLATION_VALUES as TRANSLATION_PRECISION,
  DECIMAL_PRECISION_FOR_COMPARISON_OF_SCALE_VALUES as SCALE_PRECISION,
} from '../shot-editor/constants';

/**
 * Determines the content time for a given frame.
 * First, it figures out the closest time to within a tenths of a second (flooring)
 * Then it adds time for each remainder frame.
 *
 * Important: The total time has the precision removed after 10 decimal
 * places and is then incremented slightly. This allows consumers of the data
 * to use floating point division (with Math.floor) to translate this value
 * back into a frame index.
 *
 * For example:
 * 0
 * 0 + 0.0333333334
 * 0 + 0.0666666667
 * 0 + 0.1
 * 0.1 + 0.0333333334
 * 0.1 + 0.0666666667
 * 0.1 + 0.1
 */
export const getContentTimeForFrame = (frameIndex) => {
  const seconds =
    Math.floor(frameIndex / (PLAYBACK_FRAMES_PER_SECOND / 10)) / 10;
  const leftOverFrames = frameIndex % (PLAYBACK_FRAMES_PER_SECOND / 10);
  const leftOverSeconds = leftOverFrames * FIXED_PRECISION_FRAME_DURATION;
  const result = floorForPrecision(seconds + leftOverSeconds, 10);
  // Add .0000000001 to our result, except at the start.
  return result > 0 ? result + CONTENT_TIME_PRECISION : 0;
};

/**
 * @function getContentTimeForShotRange
 * @param  {FrameRange} frameRange
 * @param {Number} offset
 * @return {Object} { startTime, endTime }
 */
export const getContentTimeForShotRange = (frameRange, offset = 0) => {
  const { start, end } = frameRange;
  // Extend content end time to fix looping issue incase endFrame time > content time End
  // Offset is the startFrame of the associated sourceElement in the case of multiple video clips
  const startTime = getContentTimeForFrame(start - offset);
  const endTime = getContentTimeForFrame(end - offset + 1);

  return {
    startTime,
    endTime,
  };
};

function createShotFillLayer(
  shotFrameRange,
  bounds,
  fillColor = DEFAULT_FILL_COLOR,
) {
  const fillLayer = Layer.createShapeLayer(SHAPE.SQUARE, fillColor);
  fillLayer.startFrame = shotFrameRange.start;
  fillLayer.endFrame = shotFrameRange.end;
  translateAndScaleShapeLayer(fillLayer, bounds);
  return fillLayer;
}

function createFillBackgroundLayer({
  creativeRectangle,
  frameRange,
  fillColor = DEFAULT_FILL_COLOR,
}) {
  const fillLayer = createShotFillLayer(
    frameRange,
    creativeRectangle,
    fillColor,
  );
  fillLayer.meta.shotType = SHOT_ELEMENT_TYPES.BACKGROUND;
  fillLayer.selectable = false;
  return fillLayer;
}

function createBlurBackgroundLayer({
  creativeRectangle,
  frameRange,
  sourceLayer,
}) {
  const backgroundLayer = createShotViewLayer(frameRange, sourceLayer);
  scaleAndCenterShotViewLayer(backgroundLayer, creativeRectangle);
  const currentTransform = backgroundLayer.transforms.get(
    DEFAULT_KEYFRAME_NUMBER,
  );
  currentTransform.filters.addBlurFilter(DEFAULT_BLUR_FILTER_VALUE_PX);
  backgroundLayer.meta.shotType = SHOT_ELEMENT_TYPES.BACKGROUND;
  backgroundLayer.selectable = false;
  return backgroundLayer;
}

export const SHOT_BACKGROUND_CONFIG = {
  [SHOT_BACKGROUND_TYPES.BLUR]: {
    contentType: CONTENT_TYPE.VIDEO,
    updateFn: createBlurBackgroundLayer,
  },
  [SHOT_BACKGROUND_TYPES.FILL]: {
    contentType: CONTENT_TYPE.SHAPE,
    updateFn: createFillBackgroundLayer,
  },
};

export const getLetterBoxLayoutHeights = (creativeRectangle) => {
  if (!Rectangle.isValidRectangle(creativeRectangle)) {
    throw new Error('Creative Rectangle must be provided to calculate heights');
  }

  const { width: creativeWidth } = creativeRectangle;

  return {
    [MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.CUSTOM]: [
      null, // null implies no specified height
    ],
    [MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.SQUARE]: [creativeWidth],
    [MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.TALL]: [
      creativeWidth / FOUR_FIVE_RATIO,
    ],
    [MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.WIDE]: [SHOT_VIEW_HEIGHT_FIT_WIDTH],
  };
};

export const getSplitVertical2LayoutHeights = (creativeRectangle) => {
  if (!Rectangle.isValidRectangle(creativeRectangle)) {
    throw new Error('Creative Rectangle must be provided to calculate heights');
  }

  const { height: creativeHeight } = creativeRectangle;
  const evenHalfSplit = (creativeHeight - SHOT_DIVIDER_HEIGHT) / 2;
  const almostTwoThirdsSplit =
    creativeHeight - SHOT_DIVIDER_HEIGHT - ALMOST_A_THIRD_SPLIT;

  return {
    [MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.CUSTOM]: [
      null, // null implies no specified height
      null, // null implies no specified height
    ],
    [MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.RATIO_1_1]: [
      evenHalfSplit,
      evenHalfSplit,
    ],
    [MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.RATIO_1_2]: [
      ALMOST_A_THIRD_SPLIT,
      almostTwoThirdsSplit,
    ],
    [MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.RATIO_2_1]: [
      almostTwoThirdsSplit,
      ALMOST_A_THIRD_SPLIT,
    ],
  };
};

export const getSplitVertical3LayoutHeights = (creativeRectangle) => {
  if (!Rectangle.isValidRectangle(creativeRectangle)) {
    throw new Error('Creative Rectangle must be provided to calculate heights');
  }

  const { height: creativeHeight } = creativeRectangle;
  const evenThirdsSplit = (creativeHeight - SHOT_DIVIDER_HEIGHT * 2) / 3;

  return {
    [MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_3.CUSTOM]: [
      evenThirdsSplit,
      evenThirdsSplit,
      evenThirdsSplit,
    ],
  };
};

export const getStaticCropLayoutHeights = (creativeRectangle) => {
  if (!Rectangle.isValidRectangle(creativeRectangle)) {
    throw new Error('Creative Rectangle must be provided to calculate heights');
  }

  const { height: creativeHeight } = creativeRectangle;
  return {
    [MEDIA_SEGMENT_EDIT_LAYOUTS.STATIC_CROP.CUSTOM]: [creativeHeight],
  };
};

// get dynamic layout heights
export const getMediaSegmentEditViewHeightsByLayoutType = (
  shotLayoutType,
  creativeRectangle,
) => {
  if (!MEDIA_SEGMENT_EDIT_TYPE[shotLayoutType]) {
    throw new Error('Invalid shot layout type');
  }

  switch (shotLayoutType) {
    case MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX:
      return getLetterBoxLayoutHeights(creativeRectangle);
    case MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2:
      return getSplitVertical2LayoutHeights(creativeRectangle);
    case MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3:
      return getSplitVertical3LayoutHeights(creativeRectangle);
    case MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP:
      return getStaticCropLayoutHeights(creativeRectangle);
    default:
      return null;
  }
};

export function addLayersForShot(
  shotFrameRange,
  mediaSegmentEdit,
  explicitMediaSegmentEditLayout,
  explicitMediaSegmentEditType,
  explicitMediaSegmentEditViewHeights,
  isShotTransformedByUser = false,
  background,
  shotSkipped,
  creativeRectangle,
  sourceLayer,
) {
  // Let's make a shot - either it didn't exist, or we got a suggestion and deleted it

  const shotGroupLayer = createGroupLayerForShot(
    shotFrameRange,
    CONTENT_TYPE.ELEMENT_GROUP,
  );
  shotGroupLayer.meta.shotType = SHOT_ELEMENT_TYPES.GROUP;
  const isShotSkipped = Boolean(shotSkipped);
  if (isShotSkipped) {
    shotGroupLayer.meta.shotSkipped = isShotSkipped;
  }

  const layersToAdd = [shotGroupLayer];
  if (isNotNil(explicitMediaSegmentEditType)) {
    shotGroupLayer.meta.layoutType = explicitMediaSegmentEditType;
  }
  if (isShotTransformedByUser) {
    shotGroupLayer.isTransformedByUser = true;
  }

  // Add a background shape
  if (ShotBackground.isValidShotBackground(background)) {
    const { updateFn } = SHOT_BACKGROUND_CONFIG[background.type];
    const backgroundFillLayer = updateFn({
      creativeRectangle,
      frameRange: shotFrameRange,
      fillColor: background.value,
      sourceLayer,
    });
    Layer.addChildLayer(shotGroupLayer, backgroundFillLayer);
    layersToAdd.push(backgroundFillLayer);
  }

  // TODO remove necessity of order here.
  // Group layer first due to how reducer currently reconciles group children.
  const mediaSegmentEditLayout = isNotNil(explicitMediaSegmentEditLayout)
    ? explicitMediaSegmentEditLayout
    : MEDIA_SEGMENT_EDIT_DEFAULT_LAYOUTS[mediaSegmentEdit.type];
  const newLayers = createLayersForShotViews(
    shotGroupLayer,
    shotFrameRange,
    mediaSegmentEdit,
    mediaSegmentEditLayout,
    explicitMediaSegmentEditViewHeights,
    creativeRectangle,
    sourceLayer,
  );
  layersToAdd.push(...newLayers);
  return layersToAdd;
}

function createLayersForShotViews(
  shotGroupLayer,
  shotFrameRange,
  mediaSegmentEdit,
  mediaSegmentEditLayout,
  explicitViewHeights,
  creativeRectangle,
  sourceLayer,
) {
  const allChildViewLayers = [];
  if (isNil(mediaSegmentEdit)) {
    mediaSegmentEdit = MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP;
    mediaSegmentEditLayout = MEDIA_SEGMENT_EDIT_LAYOUTS.STATIC_CROP.CUSTOM;
  }
  const { views: mediaSegmentViews = [] } = mediaSegmentEdit;
  let yPosition = 0;

  const mediaSegmentEditViewHeights = getMediaSegmentEditViewHeightsByLayoutType(
    mediaSegmentEdit.type,
    creativeRectangle,
  );

  // Add a layer for every view and scale/position it
  const isSingleView = mediaSegmentViews.length === 1;
  mediaSegmentViews.forEach((mediaSegmentView, index) => {
    const { startCenterX = 0 } = mediaSegmentView;
    const shotViewHeights = isNotNil(explicitViewHeights)
      ? explicitViewHeights
      : mediaSegmentEditViewHeights[mediaSegmentEditLayout];
    if (isEmptyArray(shotViewHeights)) return;

    const positionRef = {
      yPosition,
    };

    const allDescendantLayers = createLayersForShotView(
      shotGroupLayer,
      shotFrameRange,
      startCenterX,
      positionRef,
      shotViewHeights[index],
      isSingleView,
      creativeRectangle,
      sourceLayer,
    );
    yPosition = positionRef.yPosition;
    allChildViewLayers.push(...allDescendantLayers);

    // For every view but the last view, include a divider
    if (index < mediaSegmentViews.length - 1) {
      yPosition += SHOT_DIVIDER_HEIGHT;
    }
  });
  return allChildViewLayers;
}

// TODO refactor to make this doable via model objects.
function createGroupLayerForShot(shotFrameRange, contentType) {
  if (!FrameRange.isFrameRange(shotFrameRange)) return null;
  if (!isGroup(contentType)) {
    throw new Error('Cannot create group layer, content type is not a group.');
  }
  const groupContent = new Content(contentType);
  const groupLayer = new Layer(groupContent);
  groupLayer.startFrame = shotFrameRange.start;
  groupLayer.endFrame = shotFrameRange.end;
  return groupLayer;
}

// TODO refactor to make this doable via model objects.
function createLayersForShotView(
  shotGroupLayer,
  shotFrameRange,
  startCenterX,
  positionRef,
  shotViewHeight,
  isSingleView,
  creativeRectangle,
  sourceLayer,
) {
  if (!FrameRange.isFrameRange(shotFrameRange)) return null;
  if (!Layer.isLayer(sourceLayer)) return null;
  if (!Rectangle.isValidRectangle(creativeRectangle)) return null;

  const { height: creativeHeight, width: creativeWidth } = creativeRectangle;

  // Create a the view first to determine the height
  const viewVideoLayer = createShotViewLayer(shotFrameRange, sourceLayer);

  if (shotViewHeight === SHOT_VIEW_HEIGHT_FIT_WIDTH) {
    shotViewHeight =
      viewVideoLayer.height * (creativeWidth / viewVideoLayer.width);
  }

  let yPosition = positionRef.yPosition;
  if (isSingleView) {
    yPosition = creativeHeight / 2 - shotViewHeight / 2;
  }

  // for now, x will always be centered at 0
  const viewBounds = new Rectangle(0, yPosition, creativeWidth, shotViewHeight);

  // scale view layer first in case you have to return early
  scaleAndCenterShotViewLayer(viewVideoLayer, viewBounds, startCenterX);
  positionRef.yPosition = yPosition + shotViewHeight;

  // if shot view height is greater than canvas height, no need to create group
  // todo(chuti) ask omar - for consistency, it might be better to add it in group
  if (shotViewHeight >= creativeHeight) {
    Layer.addChildLayer(shotGroupLayer, viewVideoLayer);
    return [viewVideoLayer];
  }

  // create and position group layer
  const viewGroupLayer = createGroupLayerForShot(
    shotFrameRange,
    CONTENT_TYPE.ELEMENT_EFFECT_GROUP,
  );
  Layer.addChildLayer(shotGroupLayer, viewGroupLayer);
  translateViewGroupLayer(viewGroupLayer, viewBounds, creativeRectangle);

  viewVideoLayer.groupRole = GROUP_ROLES.DISPLAY;
  viewVideoLayer.selectable = false;

  // Now, create the mask!
  const viewMaskLayer = createShotFillLayer(shotFrameRange, viewBounds);
  viewMaskLayer.meta.shotType = SHOT_ELEMENT_TYPES.MASK;
  viewMaskLayer.groupRole = GROUP_ROLES.MASK;
  viewMaskLayer.blendMode = ALPHA_BLEND_MODES.DST_IN;

  // adding mask layer first so that media lays on top of it
  Layer.addChildLayer(viewGroupLayer, viewVideoLayer);
  Layer.addChildLayer(viewGroupLayer, viewMaskLayer);

  return [viewGroupLayer, viewVideoLayer, viewMaskLayer];
}

function createShotViewLayer(shotFrameRange, sourceLayer) {
  const newLayer = Layer.cloneLayer(sourceLayer);

  if (
    !Layer.isLayer(newLayer) ||
    !Content.isContent(newLayer.content) ||
    !Medium.isMedium(newLayer.medium)
  ) {
    throw new Error(
      `Failed in adding new Media and Element for shot ${shotFrameRange.toString()}`,
    );
  }

  newLayer.meta = { shotType: SHOT_ELEMENT_TYPES.VIEW };
  newLayer.startFrame = shotFrameRange.start;
  newLayer.endFrame = shotFrameRange.end;

  const frameStart = Layer.isLayer(sourceLayer) ? sourceLayer.startFrame : 0;

  const { startTime: sourceStartTime } = sourceLayer.content;
  const startTime =
    sourceStartTime + getContentTimeForFrame(shotFrameRange.start - frameStart);
  const endTime = startTime + getContentTimeForFrame(shotFrameRange.length);

  newLayer.content.audible = true;
  newLayer.content.visible = true;
  newLayer.content.startTime = startTime;
  newLayer.content.endTime = endTime;

  return newLayer;
}

function translateAndScaleShapeLayer(shapeLayer, bounds) {
  const { transforms } = shapeLayer;
  const defaultTransform = transforms.get(DEFAULT_KEYFRAME_NUMBER);
  const { scale } = defaultTransform;

  scale.x = bounds.width / shapeLayer.width;
  scale.y = bounds.height / shapeLayer.height;
}

function translateViewGroupLayer(viewGroupLayer, bounds, creativeRectangle) {
  if (!Rectangle.isValidRectangle(creativeRectangle)) return;

  const { transforms } = viewGroupLayer;
  const defaultTransform = transforms.get(DEFAULT_KEYFRAME_NUMBER);
  const { translation } = defaultTransform;

  const zeroX = -1 * (creativeRectangle.width / 2);
  const halfWidth = bounds.width / 2;
  translation.x = zeroX + halfWidth + bounds.x;

  const zeroY = -1 * (creativeRectangle.height / 2);
  const halfHeight = bounds.height / 2;
  translation.y = zeroY + halfHeight + bounds.y;
}

function scaleAndCenterShotViewLayer(shotViewLayer, bounds, centerX = 0) {
  if (!Layer.isLayer(shotViewLayer)) return;
  if (!Rectangle.isValidRectangle(bounds)) return;

  const { transforms } = shotViewLayer;
  const defaultTransform = transforms.get(DEFAULT_KEYFRAME_NUMBER);
  const { scale, translation } = defaultTransform;
  const mediumRectangle = new Rectangle(
    0,
    0,
    shotViewLayer.width,
    shotViewLayer.height,
  );
  const updatedScale = mediumRectangle.getScaleToFillIn(bounds);
  scale.x = updatedScale;
  scale.y = updatedScale;

  // calculate translation with centerX
  if (isNumber(centerX)) {
    const offset = Layer.getConstrainedAbsoluteTranslationForFrame(
      shotViewLayer,
      new Point(-centerX * updatedScale, 0),
      DEFAULT_KEYFRAME_NUMBER,
      bounds,
    );

    translation.x += isNumber(offset.x) ? offset.x : 0;
  }
}

/**
 * Calculate the min/max x/y translation bounds at a given scale
 * such that the view would fill (not fit) the bounds.
 * @param {Layer} shotViewLayer
 * @param {Rectangle} bounds
 * @param {Number} scaleX
 * @param {Number} scaleY
 * @returns {Rectangle}
 */
const calculateTranslationBoundsForShotViewAtScale = (
  shotViewLayer,
  bounds,
  scaleX,
  scaleY,
) => {
  const widthDelta = shotViewLayer.width * scaleX - bounds.width;
  const heightDelta = shotViewLayer.height * scaleY - bounds.height;
  return new Rectangle(bounds.x, bounds.y, widthDelta, heightDelta);
};

/**
 * Based on the new scale, translate the view so it won't show whitespace.
 * @param
 * @returns
 */
export const translateAfterScaleToRemoveWhitespace = (
  shotViewLayer,
  bounds,
  scale,
  translateX,
  translateY,
) => {
  const translationBounds = calculateTranslationBoundsForShotViewAtScale(
    shotViewLayer,
    bounds,
    scale,
    scale,
  );
  const {
    extrema: { bottom, left, right, top },
  } = translationBounds;
  const xViolation = left > translateX || right < translateX;
  const yViolation = top > translateY || bottom < translateY;
  if (xViolation || yViolation) {
    const boundX = clamp(
      translateX,
      translationBounds.minX,
      translationBounds.maxX,
    );
    const boundY = clamp(
      translateY,
      translationBounds.minY,
      translationBounds.maxY,
    );
    return { boundX, boundY };
  }
  return { boundX: translateX, boundY: translateY };
};

/**
 * Calculate the min/max scale (given the source media and elements size and dimension ratios)
 * and the min/max x/y translation bounds (given the current element's scale)
 * for a shot's view, such that the view would fill (not fit) the bounds.
 * @param {CreativeDocumentElement} layerElement
 * @param {Layer} shotViewLayer
 * @param {CreativeDocumentMedium} sourceMedia
 * @param {Rectangle} bounds
 * @param {Number} frameNumber
 * @returns {Constraints}
 */
export const calculateConstraintsForShotViewToFillBounds = (
  layerElement,
  shotViewLayer,
  sourceMedia,
  bounds,
  frameNumber = DEFAULT_KEYFRAME_NUMBER,
) => {
  // get rectangle/bounds for MASK element
  const minScale = getMediaRatioForBounds(
    layerElement,
    sourceMedia,
    true /* allowUpscale */,
    bounds.height,
    bounds.width,
  );
  const maxScale = minScale * MAX_SCALE_MULTIPLIER;

  const { transforms } = shotViewLayer;
  const transform = transforms.get(frameNumber);
  const { scale } = transform;
  const { x: scaleX, y: scaleY } = scale;

  const translationBounds = calculateTranslationBoundsForShotViewAtScale(
    shotViewLayer,
    bounds,
    scaleX,
    scaleY,
  );
  const {
    extrema: { bottom, left, right, top },
  } = translationBounds;
  return new Constraints(
    minScale,
    minScale,
    maxScale,
    maxScale,
    left,
    top,
    right,
    bottom,
  );
};

export function getFrameRangesNotRepresentedByLayers(layers, frameRange) {
  if (isEmptyArray(layers)) return [frameRange];
  const unrepresentedFrameRanges = frameRange.relativeComplementIn(
    layers.map((layer) => new FrameRange(layer.startFrame, layer.endFrame)),
  );

  return unrepresentedFrameRanges;
}

/**
 * This accepts a list of shots, orders them by start_frame and removes any duplicates.
 * It prefers the dupe that has been analyzed.
 * @param {shotWireObject[]} shotList
 * @param {String} creativeId
 * @param {String} sourceMediaServerId
 * @returns {shotWireObject[]} a ordered and deduped shot list
 */
export function sanitizeShotListWireObjects(
  shotListWireObjects,
  creativeId,
  sourceMediaServerId,
) {
  const orderedShotList = sortByProperties(shotListWireObjects, [
    { key: 'start_frame' },
    { key: 'end_frame' },
  ]);
  const dedupedShotList = [];
  let hasDuplicates = false;
  let mostRecentlyAddedShot = null;
  orderedShotList.forEach((shot) => {
    if (isEmptyArray(dedupedShotList)) {
      dedupedShotList.push(shot);
      mostRecentlyAddedShot = shot;
      return;
    }
    // Dedupe - since sorted by start/end, if previous shot matches, ignore shot.
    if (
      mostRecentlyAddedShot.start_frame === shot.start_frame &&
      mostRecentlyAddedShot.end_frame === shot.end_frame
    ) {
      hasDuplicates = true;
      // if the previous shot is already analyzed ignore this one.
      // if it isn't and this one isn't analyzed, also ignore it.
      const dupeIsAnalyzedFirst =
        shot.shot_analysis_complete &&
        !mostRecentlyAddedShot.shot_analysis_complete;
      if (!dupeIsAnalyzedFirst) {
        return;
      }
      // replace the existing shot with this one.
      dedupedShotList[dedupedShotList.length - 1] = shot;
    } else {
      dedupedShotList.push(shot);
    }
    mostRecentlyAddedShot = shot;
  });
  if (hasDuplicates) {
    captureDebugOnce('Shot List Integrity - Duplicate shots', {
      creativeId,
      sourceMediaServerId,
    });
  }
  return dedupedShotList;
}

export function adjustShotListWireObjects(
  shotListWireObjects,
  framesToAdjustBy,
) {
  if (isNotNumber(framesToAdjustBy) || framesToAdjustBy === 0) {
    return shotListWireObjects;
  }
  const shotList = [];
  shotListWireObjects.forEach((shot) => {
    shot.start_frame = shot.start_frame + framesToAdjustBy;
    shot.end_frame = shot.end_frame + framesToAdjustBy;
    shotList.push(shot);
  });
  return shotList;
}

const chapterUtilWorkerService = new ChapterUtilWorkerService();
export function getChapters(
  shots,
  targetChapterLength = DEFAULT_CHAPTER_LENGTH_TARGET_SECOND,
  maxChapterLength = CHAPTER_LENGTH_MAX_SECOND,
) {
  return chapterUtilWorkerService
    .getChapters(shots, targetChapterLength, maxChapterLength)
    .catch((error) => {
      Log.warn('populateChapterBreaks', error);
      captureDebug('populateChapterBreaks', {
        error,
        shots: JSON.stringify(shots),
      });

      // fallback to the main thread when there is an error in the worker
      return getChapterBreaks(shots, targetChapterLength, maxChapterLength);
    });
}

/**
 * Calculates the current ratio values for the splits between screens.
 * Also determines the min/max ratio values for these splits such that
 * whitespace is avoided.
 * @returns [ClampedNumber] a list of ClampedNumbers, one for each split between screens.
 */
export function getCustomSplitMetadata(
  shotViewLayers,
  shotMaskHeights,
  creativeRectangle,
) {
  if (!Rectangle.isValidRectangle(creativeRectangle)) return null;
  const { height: creativeHeight, width: creativeWidth } = creativeRectangle;

  const numShotDividers = shotMaskHeights.length - 1;
  const remainingHeight =
    creativeHeight - SHOT_DIVIDER_HEIGHT * numShotDividers;

  const activeShotViewRatios = isEmptyArray(shotMaskHeights)
    ? SINGLE_VIEW_FULL_HEIGHT_RATIO // When the view is the full size of the canvas, there is no mask layer
    : shotMaskHeights.map((height) => height / remainingHeight);

  // Keep track of where to start the next ratio divider.
  let sumViewRatios = 0;
  const numSplits = Math.max(1, activeShotViewRatios.length - 1);
  const customSplitMetadata = [];

  for (let i = 0; i < numSplits; i++) {
    const layer = shotViewLayers[i];
    const minHeight = (creativeWidth / layer.width) * layer.height;
    const minRatio = minHeight / remainingHeight;
    const adjustedMinRatio = sumViewRatios + minRatio;

    let maxRatio = 1;

    // if there is an adjacent view that this split controls
    if (activeShotViewRatios.length > i + 1) {
      // calulate the height ratios of the remaining views that aren't affected by this split.
      const remainingRatioTotalAfterNextView = activeShotViewRatios.reduce(
        (acc, cur, index) => {
          if (index > i + 1) {
            acc += cur;
          }
          return acc;
        },
        0,
      );

      // Max ratio is such that the subsequent screen can shrink to its min ratio
      // while also considering the remaining height of the untouched screens.
      const nextLayer = shotViewLayers[i + 1];
      const nextMinHeight =
        (creativeWidth / nextLayer.width) * nextLayer.height;
      const nextMinRatio = nextMinHeight / remainingHeight;
      maxRatio -= nextMinRatio + remainingRatioTotalAfterNextView;
    }

    sumViewRatios += activeShotViewRatios[i];

    const splitMetadata = new ClampedNumber(
      sumViewRatios,
      new Interval(adjustedMinRatio, maxRatio),
    );
    customSplitMetadata.push(splitMetadata);
  }
  return customSplitMetadata;
}

export function calculateAdjustedShotMaskHeights(
  shotViewLayers,
  shotMaskHeights,
  viewIndex,
  targetRatio,
  creativeRectangle,
) {
  if (!Rectangle.isValidRectangle(creativeRectangle)) return 0;
  const { height: creativeHeight } = creativeRectangle;

  let ratioDelta = 0;
  const numDividers = shotMaskHeights.length - 1;
  const remainingVerticalSpace =
    creativeHeight - SHOT_DIVIDER_HEIGHT * numDividers;

  return shotViewLayers.map((_, index) => {
    const existingHeight = isEmptyArray(shotMaskHeights)
      ? creativeHeight
      : shotMaskHeights[index];
    const existingRatio = existingHeight / remainingVerticalSpace;
    let viewRatio = existingRatio;
    if (index === viewIndex) {
      viewRatio = targetRatio;
      ratioDelta = viewRatio - existingRatio;
    }
    if (index === viewIndex + 1) {
      viewRatio = existingRatio - ratioDelta;
    }
    return viewRatio * remainingVerticalSpace;
  });
}

/**
 * @function updateCurrentRangesForMetaSelect
 * @param {array} currentRanges side effect: currentRanges is updated to account for given range
 * @param {FrameRange} selectedRange the selection
 * @return {bool} true if shot was selected, false if shot was unselected
 */
export function updateCurrentRangesForMetaSelect(currentRanges, selectedRange) {
  // If user just tried to unselect the only shot, do nothing
  if (currentRanges.length === 1 && currentRanges[0].equals(selectedRange)) {
    return false;
  }

  // If the selected shot is inside a selected range, then remove this shot from that range
  let isNotContainedInSomeSelection = true;
  for (let i = 0; i < currentRanges.length; i += 1) {
    const currentRange = currentRanges[i];
    if (!FrameRange.isFrameRange(currentRange)) return false;

    // The shot that was just selected can be either inside or outside an existing selection range. If
    // it's inside a selection range, then it's either (1) snug on its left, (2) snug on its right,
    // (3) a proper subset of it, or (4) equal to it.
    if (currentRange.contains(selectedRange)) {
      if (
        currentRange.start === selectedRange.start &&
        selectedRange.end < currentRange.end
      ) {
        // (1) left
        currentRanges.splice(
          i,
          1,
          new FrameRange(selectedRange.end + 1, currentRange.end),
        );
      } else if (
        currentRange.start < selectedRange.start &&
        selectedRange.end === currentRange.end
      ) {
        // (2) right
        currentRanges.splice(
          i,
          1,
          new FrameRange(currentRange.start, selectedRange.start - 1),
        );
      } else if (
        currentRange.start < selectedRange.start &&
        selectedRange.end < currentRange.end
      ) {
        // (3) inside
        const firstNewRange = new FrameRange(
          currentRange.start,
          selectedRange.start - 1,
        );
        const secondNewRange = new FrameRange(
          selectedRange.end + 1,
          currentRange.end,
        );
        currentRanges.splice(i, 1, firstNewRange, secondNewRange);
      } else {
        // (4) equal
        currentRanges.splice(i, 1);
      }
      isNotContainedInSomeSelection = false;
      break;
    }
  }

  // If it's outside all selection ranges, then it's either (1) snugly between two, (2) snug on the
  // right side of one, (3) snug on the left side of one, or (4) not adjacent to any existing
  // selection range.
  if (isNotContainedInSomeSelection) {
    let isAdjacentToSomeSelection = false;
    for (let i = 0; i < currentRanges.length; i += 1) {
      const nextRange =
        i < currentRanges.length - 1 ? currentRanges[i + 1] : null;
      const currentRange = currentRanges[i];

      if (
        isNotNil(nextRange) &&
        currentRange.end + 1 === selectedRange.start &&
        selectedRange.end + 1 === nextRange.start
      ) {
        // (1)
        currentRanges.splice(
          i,
          2,
          new FrameRange(currentRange.start, nextRange.end),
        );
        isAdjacentToSomeSelection = true;
        break;
      } else if (currentRange.end + 1 === selectedRange.start) {
        // (2) right
        currentRanges.splice(
          i,
          1,
          new FrameRange(currentRange.start, selectedRange.end),
        );
        isAdjacentToSomeSelection = true;
        break;
      } else if (selectedRange.end + 1 === currentRange.start) {
        // (3) left
        currentRanges.splice(
          i,
          1,
          new FrameRange(selectedRange.start, currentRange.end),
        );
        isAdjacentToSomeSelection = true;
        break;
      }
    }
    if (!isAdjacentToSomeSelection) {
      currentRanges.push(selectedRange);
    }
  }
  return isNotContainedInSomeSelection;
}

/**
 * @function updateCurrentRangesForReselect
 * @param {array} currentRanges side effect: currentRanges is updated to account for given range
 * @param {Number} indexIntoShotRanges the index of the selection box whose drag handle is being dragged
 * @param {FrameRange} selectedRange the selection
 * @param {bool} isForBottom
 */
export function updateCurrentRangesForReselect(
  currentRanges,
  indexIntoShotRanges,
  selectedRange,
  isForBottom,
) {
  const originalShotRange = currentRanges[indexIntoShotRanges];
  if (isForBottom) {
    if (originalShotRange.end < selectedRange.start) {
      // bottom handle went down
      let idx = indexIntoShotRanges + 1;
      let landedBefore = true;
      while (idx < currentRanges.length) {
        const currentRange = currentRanges[idx];
        if (selectedRange.end + 1 < currentRange.start) {
          break;
        }
        if (
          currentRange.contains(selectedRange) ||
          selectedRange.end + 1 === currentRange.start
        ) {
          landedBefore = false;
          break;
        }
        idx += 1;
      }
      // If selection landed after last range or selection landed before current range
      if (idx === currentRanges.length || landedBefore) {
        currentRanges.splice(
          indexIntoShotRanges,
          idx - indexIntoShotRanges,
          new FrameRange(originalShotRange.start, selectedRange.end),
        );
      } else {
        // if selection landed inside current range
        const rangeWeAreMergingWith = currentRanges[idx];
        currentRanges.splice(
          indexIntoShotRanges,
          idx - indexIntoShotRanges + 1,
          new FrameRange(originalShotRange.start, rangeWeAreMergingWith.end),
        );
      }
    } else {
      // bottom handle went up
      if (selectedRange.start < originalShotRange.start) return;
      currentRanges.splice(
        indexIntoShotRanges,
        1,
        new FrameRange(originalShotRange.start, selectedRange.end),
      );
    }
  } else {
    // is for top
    if (selectedRange.end < originalShotRange.start) {
      // top handle went up
      let idx = indexIntoShotRanges - 1;
      let landedAfter = true;
      while (idx > -1) {
        const currentRange = currentRanges[idx];
        if (currentRange.end < selectedRange.start - 1) {
          break;
        }
        if (
          currentRange.contains(selectedRange) ||
          currentRange.end === selectedRange.start - 1
        ) {
          landedAfter = false;
          break;
        }
        idx -= 1;
      }
      // If selection landed before first range or selection landed after current range
      if (idx === -1 || landedAfter) {
        currentRanges.splice(
          idx + 1,
          indexIntoShotRanges - idx,
          new FrameRange(selectedRange.start, originalShotRange.end),
        );
      } else {
        // if selection landed inside current range
        const rangeWeAreMergingWith = currentRanges[idx];
        currentRanges.splice(
          idx,
          indexIntoShotRanges - idx + 1,
          new FrameRange(rangeWeAreMergingWith.start, originalShotRange.end),
        );
      }
    } else {
      // top handle went down
      if (selectedRange.start > originalShotRange.end) return;
      currentRanges.splice(
        indexIntoShotRanges,
        1,
        new FrameRange(selectedRange.start, originalShotRange.end),
      );
    }
  }
}

export function upgradeSourceAudioToShotAudio(creativeData) {
  const { content = {}, elements = {}, media = {} } = creativeData;

  const { contentById = {} } = content;
  const { elementsById = {} } = elements;
  const { mediaById = {} } = media;

  const elementKeys = Object.keys(elementsById);

  // Find source audio layer and determine if audible is set.
  const keyForSourceLayerWithAudio = elementKeys.find((elementKey) => {
    const element = elementsById[elementKey];
    if (isNil(element)) return false;

    const { contentId, meta } = element;
    if (isNil(meta)) return false;
    if (isNil(contentId)) return false;

    const { shotType } = meta;
    if (shotType !== SHOT_ELEMENT_TYPES.SOURCE) return false;

    const content = contentById[contentId];
    if (isNil(content)) return false;

    const { audible } = content;
    return Boolean(audible);
  });

  // If it isn't, then just return. Nothing needs to change.
  if (isNil(keyForSourceLayerWithAudio)) return creativeData;

  const sourceElement = elementsById[keyForSourceLayerWithAudio];
  const { contentId } = sourceElement;
  const sourceContent = contentById[contentId];
  const { mediaId } = sourceContent;
  const sourceMedia = mediaById[mediaId];

  const { audible } = sourceContent;
  const {
    audioServerId,
    mediaType: sourceMediaType,
    processedAudio,
    serverId,
  } = sourceMedia;
  if (
    sourceMediaType !== MEDIA_TYPE.AUDIO &&
    sourceMediaType !== MEDIA_TYPE.VIDEO
  ) {
    return creativeData;
  }

  sourceContent.audible = false;

  elementKeys.forEach((elementKey) => {
    const element = elementsById[elementKey];
    if (isNil(element)) return;

    const { contentId, meta } = element;
    if (isNil(meta)) return;
    if (isNil(contentId)) return;

    const { shotType } = meta;
    if (shotType !== SHOT_ELEMENT_TYPES.VIEW) return;

    const content = contentById[contentId];
    if (isNil(content)) return;

    const { mediaId } = content;
    if (isNil(mediaId)) return;

    const media = mediaById[mediaId];
    if (isNil(media)) return;

    const { mediaType } = media;
    if (mediaType !== MEDIA_TYPE.AUDIO && mediaType !== MEDIA_TYPE.VIDEO) {
      return;
    }

    content.audible = audible;
    media.audioServerId = audioServerId;
    media.processedAudio = processedAudio;
    media.serverId = serverId;
  });

  return creativeData;
}

export const getConstraintsByShotViewElementIdHelper = (
  shotViewElement,
  shotViewLayer,
  sourceMedia,
  bounds,
  frameNumber = DEFAULT_KEYFRAME_NUMBER,
) => {
  if (
    isNil(shotViewElement) ||
    isNil(shotViewLayer) ||
    isNil(sourceMedia) ||
    isNil(bounds)
  ) {
    return null;
  }
  return calculateConstraintsForShotViewToFillBounds(
    shotViewElement,
    shotViewLayer,
    sourceMedia,
    bounds,
    frameNumber,
  );
};

export const getBoundsByShotViewElementIdHelper = (
  shotViewElement,
  allMaskLayersByElementEffectGroupId,
  creativeRectangle,
) => {
  // If there's no mask layers, derive bounds from canvas
  const { groupId } = shotViewElement;
  const layer = allMaskLayersByElementEffectGroupId.get(groupId);
  if (isNil(layer)) {
    return creativeRectangle;
  }
  return Layer.getLocalBoundsForFrame(layer, DEFAULT_KEYFRAME_NUMBER);
};

export const generateTransformInfoFromLayers = (
  layersArray,
  elementMap,
  allMaskLayersByElementEffectGroupId,
  contentById,
  mediaById,
  sourceMedia,
  creativeRectangle,
) => {
  const metadataArray = [];
  layersArray.forEach((layerArrayAcrossAllSelectedShots) => {
    if (isEmptyArray(layerArrayAcrossAllSelectedShots)) return;
    const frameNumberToMetaData = {};
    const { elementId: metadataId } = layerArrayAcrossAllSelectedShots[0];
    let previousScale;
    const isInPanningMode = layerArrayAcrossAllSelectedShots.length === 1;
    let metadata = new MediaSegmentTransformationControlMetadata();
    layerArrayAcrossAllSelectedShots.forEach((layer, i) => {
      const elementId = layer.elementId;
      if (isNil(elementId)) return;
      const element = getByIdHelper(elementMap, elementId);
      if (isNil(element)) return;
      const { transforms } = element;
      const transformFrames = Object.keys(transforms);
      transformFrames.forEach((frameNumber) => {
        if (isInPanningMode) {
          metadata = new MediaSegmentTransformationControlMetadata();
        }
        metadata.metadataId = metadataId;
        metadata.pushElementId(elementId);
        const currentTransform = transforms[frameNumber];
        const contentId = getContentIdHelper(element);
        const content = getContentHelper(contentId, contentById);
        const currentBounds = getBoundsByShotViewElementIdHelper(
          element,
          allMaskLayersByElementEffectGroupId,
          creativeRectangle,
        );
        metadata.pushBounds(currentBounds);

        const media = getMediaHelper(
          elementMap,
          elementId,
          contentById,
          mediaById,
        );
        const shotViewLayer = getLayerByIdHelper(element, content, media);
        const parsedFrameNumber = parseInt(frameNumber, 10);
        const currentConstraints = getConstraintsByShotViewElementIdHelper(
          element,
          shotViewLayer,
          sourceMedia,
          currentBounds,
          parsedFrameNumber,
        );
        metadata.pushConstraints(currentConstraints);

        const {
          alpha,
          easingFunction,
          rotation,
          scale: { x: scale }, // element's scaleX and scaleY should always be ===
          translation: { x: translateX, y: translateY },
        } = currentTransform;

        if (
          !isInPanningMode &&
          isNotNil(previousScale) &&
          !areNumbersEqualToDecimal(scale, previousScale, SCALE_PRECISION)
        ) {
          metadata.isScaleMixed = true;
        }
        if (
          !isInPanningMode &&
          i > 0 &&
          !areNumbersEqualToDecimal(
            translateX,
            metadata.translateXValues[i - 1],
            TRANSLATION_PRECISION,
          )
        ) {
          metadata.isTranslateXMixed = true;
        }
        if (
          !isInPanningMode &&
          i > 0 &&
          !areNumbersEqualToDecimal(
            translateY,
            metadata.translateYValues[i - 1],
            TRANSLATION_PRECISION,
          )
        ) {
          metadata.isTranslateYMixed = true;
        }
        metadata.pushTranslateXValue(translateX);
        metadata.pushTranslateYValue(translateY);
        metadata.scale = scale;
        metadata.alpha = alpha;
        metadata.easingFunction = easingFunction;
        metadata.rotation = rotation;
        previousScale = scale;
        frameNumberToMetaData[frameNumber] = metadata;
      });
    });
    metadataArray.push(frameNumberToMetaData);
  });
  return metadataArray;
};

export const getSourceVideoCreativeLayerFromFrameNumber = (
  sourceVideoCreativeLayersByFrameRange,
  frameNumber,
) => {
  if (isNil(sourceVideoCreativeLayersByFrameRange)) {
    return null;
  }

  for (const [
    frameRange,
    sourceVideoCreativeLayer,
  ] of sourceVideoCreativeLayersByFrameRange.entries()) {
    if (
      FrameRange.isFrameRange(frameRange) &&
      frameRange.containsFrameNumber(frameNumber)
    ) {
      return sourceVideoCreativeLayer;
    }
  }

  return null;
};

export const shiftLayersStartEndFramesByAmount = (layers, shiftAmount) => {
  if (isEmptyArray(layers)) return;

  layers.forEach((layer) => {
    if (!Layer.isLayer(layer)) return;
    const transforms = layer.transforms;
    const isLayerUsingPanning = transforms.size >= 2;

    layer.startFrame = layer.startFrame + shiftAmount;
    layer.endFrame = layer.endFrame + shiftAmount;
    layer.isTransformedByUser = true;

    if (!isLayerUsingPanning) return;

    const transformKeyFrames = [...transforms.keys()];
    const shiftedTransforms = new Map();

    transformKeyFrames.forEach((frameNumber) => {
      const transform = transforms.get(frameNumber);
      shiftedTransforms.set(frameNumber + shiftAmount, transform);
    });

    layer.transforms = shiftedTransforms;
  });
};

/**
 * @function adjustOutputRangesToBeContiguous
 * Ensures each output range is contiguous. Note that this purposefully only shifts the end frame to make it
 * contiguous as the start frames of outputRanges must match the start frame of a shot container.
 * @param  {Object[]} outputRanges
 * @param  {number} rootEndFrame
 */
export const adjustOutputRangesToBeContiguous = (
  outputRanges,
  rootEndFrame,
) => {
  if (isEmptyArray(outputRanges) || isNotNumber(rootEndFrame)) return [];

  const numOutputRanges = outputRanges.length;
  const lastIndex = numOutputRanges - 1;
  outputRanges[0].start = 0;
  outputRanges[lastIndex].end = rootEndFrame;

  for (let i = 1; i < numOutputRanges; i++) {
    const prevOutputRange = outputRanges[i - 1];
    const currOutputRange = outputRanges[i];
    if (prevOutputRange.end !== currOutputRange.start - 1) {
      prevOutputRange.end = currOutputRange.start - 1;
    }
  }

  return outputRanges;
};

/**
 * @function buildUpdatedOutputRanges
 * Adjusts the chapter breaks by updating and removing the chapter breaks specified
 * @param  {FrameRange[]} outputRanges
 * @param  {FrameRange[]} shiftedOutputRanges - outputRanges after the adjusted shiftAmount
 * @param  {number[]} chapterIndicesToRemove - indices of outputRanges to be removed
 * @param  {number[]} chapterIndicesToUpdate - indices of outputRanges to be updated
 * @return {Object[]} sortedUpdatedRanges
 */
export const buildUpdatedOutputRanges = (
  outputRanges,
  shiftedOutputRanges,
  chapterIndicesToRemove,
  chapterIndicesToUpdate,
) => {
  const finalOutputRangesUnsorted = [];

  for (let i = 0; i < outputRanges.length; i++) {
    const indexToUpdate = chapterIndicesToUpdate.indexOf(i);
    const indexToRemove = chapterIndicesToRemove.indexOf(i);
    let outputFrameRange = outputRanges[i];

    // Check if this outputRange has been shifted or should be removed
    if (indexToUpdate !== -1 && indexToRemove === -1) {
      const index = chapterIndicesToUpdate[indexToUpdate];
      const shiftedIndex = chapterIndicesToUpdate.indexOf(index);
      outputFrameRange = shiftedOutputRanges[shiftedIndex];
    } else if (indexToRemove !== -1) {
      outputFrameRange = null;
    }

    // Build the final unsorted outputRange without nulls
    if (!FrameRange.isFrameRange(outputFrameRange)) continue;
    finalOutputRangesUnsorted.push(outputFrameRange.toJSON());
  }

  return sortBy(finalOutputRangesUnsorted, 'start');
};
